import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { SellerContractService } from '@/services/sellerContract/sellerContractService';
import {
  CreateSellerContractRequest,
  ManualSellerContractSignatureMethod,
} from '@/types/sellerContract';

export const useSellerContract = () => {
  const queryClient = useQueryClient();

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['seller-contracts'] });
    queryClient.invalidateQueries({ queryKey: ['listings'] });
  };

  const useGetAllSellerContracts = (
    page: number = 1,
    limit: number = 50,
    status?: string,
    search?: string,
  ) =>
    useQuery({
      queryKey: ['seller-contracts', 'list', page, limit, status, search],
      queryFn: () => SellerContractService.getAllSellerContracts(page, limit, status, search),
      staleTime: 0,
      gcTime: 0,
    });

  const useGetSellerContractByListing = (listingId?: string | null) =>
    useQuery({
      queryKey: ['seller-contracts', 'by-listing', listingId],
      queryFn: () =>
        SellerContractService.getSellerContractByListingId(listingId as string),
      enabled: Boolean(listingId),
      staleTime: 0,
    });

  const createSellerContractMutation = useMutation({
    mutationFn: ({
      request,
      replaceExisting,
    }: {
      request: CreateSellerContractRequest;
      replaceExisting?: boolean;
    }) => SellerContractService.createSellerContract(request, replaceExisting),
    onSuccess: invalidate,
  });

  const deleteSellerContractMutation = useMutation({
    mutationFn: (id: string) => SellerContractService.deleteSellerContract(id),
    onSuccess: invalidate,
  });

  const sendSellerContractMutation = useMutation({
    mutationFn: (id: string) => SellerContractService.sendSellerContract(id),
    onSuccess: invalidate,
  });

  const requestSignatureMutation = useMutation({
    mutationFn: (id: string) => SellerContractService.requestSignature(id),
    onSuccess: invalidate,
  });

  const markSignatureMutation = useMutation({
    mutationFn: ({
      id,
      method,
      note,
    }: {
      id: string;
      method: ManualSellerContractSignatureMethod;
      note?: string;
    }) => SellerContractService.markSignature(id, method, note),
    onSuccess: invalidate,
  });

  return {
    useGetAllSellerContracts,
    useGetSellerContractByListing,

    createSellerContractAsync: createSellerContractMutation.mutateAsync,
    isCreatingSellerContract: createSellerContractMutation.isPending,

    deleteSellerContractAsync: deleteSellerContractMutation.mutateAsync,
    isDeletingSellerContract: deleteSellerContractMutation.isPending,

    sendSellerContractAsync: sendSellerContractMutation.mutateAsync,
    isSendingSellerContract: sendSellerContractMutation.isPending,

    requestSignatureAsync: requestSignatureMutation.mutateAsync,
    isRequestingSignature: requestSignatureMutation.isPending,

    markSignatureAsync: markSignatureMutation.mutateAsync,
    isMarkingSignature: markSignatureMutation.isPending,
  };
};
